/**
 * LocationPicker — reusable geo picker.
 *
 * • "Use my location" button → browser geolocation → POST /geo/reverse
 * • Debounced text input → GET /geo/autocomplete (Google if configured,
 *   Nominatim fallback otherwise)
 * • Selected place is persisted to localStorage under FP_LOC_KEY so the
 *   entire site remembers the shopper's city.
 *
 * Emits onChange({ latitude, longitude, city, formatted_address })
 * whenever the user picks a location.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Crosshair, X, Search } from "lucide-react";
import { http } from "@/lib/api";

export const FP_LOC_KEY = "fp:userLocation";

export function getStoredLocation() {
  try {
    const raw = localStorage.getItem(FP_LOC_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function saveStoredLocation(loc) {
  try {
    if (loc) localStorage.setItem(FP_LOC_KEY, JSON.stringify(loc));
    else     localStorage.removeItem(FP_LOC_KEY);
  } catch { /* ignore */ }
}

export default function LocationPicker({ value, onChange, compact = false }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [findingMe, setFindingMe] = useState(false);
  const sessionRef = useRef(crypto?.randomUUID?.() || String(Date.now()));
  const timer = useRef();

  // Debounced autocomplete
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setItems([]); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await http.get("/geo/autocomplete", {
          params: { input: q.trim(), session_token: sessionRef.current, limit: 6 },
        });
        setItems(r.data?.suggestions || []);
      } catch { setItems([]); }
      setBusy(false);
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const pick = useCallback(async (item) => {
    let loc = null;
    if (item.latitude != null && item.longitude != null) {
      // Nominatim: coords are inline
      loc = {
        latitude: item.latitude, longitude: item.longitude,
        formatted_address: item.description,
        city: item.description.split(",")[0]?.trim(),
      };
    } else {
      // Google: need to hit /geo/place/{id}
      try {
        const r = await http.get(`/geo/place/${encodeURIComponent(item.place_id)}`,
          { params: { session_token: sessionRef.current } });
        loc = r.data;
      } catch { /* ignore */ }
    }
    if (loc) {
      saveStoredLocation(loc);
      onChange?.(loc);
      setQ(loc.city || loc.formatted_address || "");
      setItems([]);
      sessionRef.current = crypto?.randomUUID?.() || String(Date.now());
    }
  }, [onChange]);

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setFindingMe(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const r = await http.post("/geo/reverse", {
            latitude: coords.latitude, longitude: coords.longitude,
          });
          const loc = r.data;
          saveStoredLocation(loc);
          onChange?.(loc);
          setQ(loc.city || loc.formatted_address || "");
        } catch { /* ignore */ }
        setFindingMe(false);
      },
      () => setFindingMe(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  }, [onChange]);

  const clear = () => {
    saveStoredLocation(null);
    onChange?.(null);
    setQ(""); setItems([]);
  };

  return (
    <div className="relative w-full" data-testid="location-picker">
      <div className="flex items-center gap-2 rounded-full border-2 bg-white px-3 py-2"
           style={{ borderColor: "#E9D8B4" }}>
        <MapPin className="w-4 h-4 opacity-60 flex-shrink-0" />
        <input
          value={q || (value?.city ?? "")}
          onChange={(e) => setQ(e.target.value)}
          placeholder={compact ? "City" : "Enter your city…"}
          className="flex-1 outline-none bg-transparent text-sm min-w-0"
          data-testid="location-input"
        />
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin opacity-50" />}
        {value?.city && !q && (
          <button onClick={clear} className="opacity-60 hover:opacity-100" title="Clear">
            <X className="w-4 h-4" />
          </button>
        )}
        <button
          type="button" onClick={useMyLocation} disabled={findingMe}
          className="flex-shrink-0 rounded-full p-1.5 hover:bg-emerald-50 disabled:opacity-40"
          title="Use my location"
          data-testid="location-use-me"
        >
          {findingMe ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
        </button>
      </div>
      {items.length > 0 && (
        <ul className="absolute z-40 top-full mt-2 left-0 right-0 bg-white rounded-2xl border shadow-xl overflow-hidden"
            style={{ borderColor: "#E9D8B4" }} data-testid="location-suggestions">
          {items.map((it) => (
            <li key={it.place_id}>
              <button onClick={() => pick(it)}
                      className="w-full text-left px-3 py-2 hover:bg-black/5 text-sm flex items-center gap-2"
                      data-testid={`location-suggestion-${it.place_id}`}>
                <Search className="w-3 h-3 opacity-40 flex-shrink-0" />
                <span className="truncate">{it.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
